const callAfterDomUpdate = (fn: () => void): void => {
  const intermediate = () => window.requestAnimationFrame(fn)
  window.requestAnimationFrame(intermediate)
}

const lazyFnCache = new WeakMap<(...args: any[]) => any, (...args: any[]) => void>()
const lazyFn = <T extends (...args: any[]) => any>(fn: T, signal?: AbortSignal): ((...args: Parameters<T>) => void) => {
  if (import.meta.server) {
    return fn
  }

  if (!fn) {
    return () => {}
  }

  if (signal) {
    return (...args: Parameters<T>) => callAfterDomUpdate(() => !signal.aborted && fn(...args))
  }

  if (lazyFnCache.has(fn)) {
    return lazyFnCache.get(fn)!
  }

  const wrapped = (...args: Parameters<T>) => callAfterDomUpdate(() => fn(...args))
  lazyFnCache.set(fn, wrapped)
  return wrapped
}

const waitForNextPaint = (): Promise<void> => {
  return new Promise((resolve) => {
    if (import.meta.server) {
      resolve()
      return
    }

    if (document.visibilityState === 'hidden') {
      setTimeout(resolve)
      return
    }

    const resolveAndStopListening = () => {
      document.removeEventListener('visibilitychange', resolveAndStopListening)
      resolve()
    }

    document.addEventListener('visibilitychange', resolveAndStopListening)
    window.requestAnimationFrame(() => setTimeout(resolveAndStopListening))
  })
}

const waitForDocumentIdle = (delay = 500): Promise<void> => {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      return window.requestIdleCallback(() => setTimeout(resolve, delay))
    }

    return setTimeout(resolve, 300 + delay)
  })
}

const callAfterDocumentIdle = (func: () => void, delay?: number): void => {
  waitForDocumentIdle(delay).then(func)
}

const isElementInViewport = (element: Element): boolean => {
  const box = element.getBoundingClientRect()

  return box.bottom > 0 && box.top < window.innerHeight
}

export {
  callAfterDomUpdate,
  lazyFn,
  waitForNextPaint,
  waitForDocumentIdle,
  callAfterDocumentIdle,
  isElementInViewport,
}
